import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Store, ChevronsUpDown, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useSelection } from '@/contexts/SelectionContext';
import { useContextTree } from '@/hooks/useContextTree';
import { guardedSelectionChange } from '@/lib/pendingEditsGuard';
import { formatFeedBadge, getFeedTooltip } from '@/lib/feedLabels';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { FeedSwitcherList, type FeedSwitcherCloseOptions } from './FeedSwitcherList';
import { useIsBelowBreakpoint } from './useIsBelowBreakpoint';

const SYNC_FRESH_MS = 24 * 60 * 60 * 1000;
/** Popover vs. Drawer (Muster `use-mobile.tsx`, siehe useIsBelowBreakpoint.ts). */
const DRAWER_BREAKPOINT = 768;

/**
 * Die Kontext-Pille: EINE Zeile statt drei Auswahlfelder (Task 5, "Eine-Zeile-
 * Feed-Switcher"). Hat `ContextSelector` vollstaendig abgeloest — die alte
 * Komponente ist auf diesem Branch entfernt. Liest die geschlossene Pille
 * ausschliesslich aus `useSelection()`, der Baum aus `useContextTree` wird
 * erst geladen, sobald das Popover/Drawer das erste Mal geoeffnet wird
 * (lazy), bleibt danach aber aktiv (`hasOpened`), damit ein Wiederoeffnen
 * nicht erneut leer startet.
 */
interface FeedSwitcherProps {
  /**
   * UX-Audit A3-09 (Option 3A): jeder Anstieg dieses Zaehlers oeffnet den
   * Switcher von aussen. Ausloeser ist der Feed-Schritt der Fortschrittsleiste
   * auf der Startseite, dessen Ziel `/` die Seite waere, auf der man schon
   * steht. 0 (Vorgabe) oeffnet nichts.
   */
  openRequest?: number;
}

export const FeedSwitcher: React.FC<FeedSwitcherProps> = ({ openRequest = 0 }) => {
  const { t, i18n } = useTranslation('dashboard');
  const { selectedMerchantCenterId, selectedFeedId, merchantCenter, feed, loading, setSelection } = useSelection();

  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);
  useEffect(() => {
    if (openRequest > 0) setOpen(true);
  }, [openRequest]);

  const { accounts, isLoading: treeLoading, isError: treeError, detailsFailed } = useContextTree(hasOpened);
  const isDrawer = useIsBelowBreakpoint(DRAWER_BREAKPOINT);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * M7 (Fix-Runde 2), zweiter Teil des Fixes: Radix/vaul haben ein EIGENES
   * Default-Verhalten, das den Fokus beim Schliessen auf den Trigger
   * zurueckwirft (`onCloseAutoFocus`) — unabhaengig davon, ob unser eigener
   * `requestAnimationFrame`-Restore unten laeuft oder nicht. Nur den eigenen
   * Restore zu ueberspringen reicht darum NICHT: Radix holt sich den Fokus
   * sonst trotzdem zurueck. Der Ref haelt fest, dass GENAU der naechste
   * `onCloseAutoFocus`-Aufruf abgefangen werden soll (Navigate-Pfad), ohne den
   * Normalfall (erfolgreicher Feed-Wechsel, Restore gewuenscht) zu aendern.
   */
  const skipCloseAutoFocusRef = useRef(false);

  const handleSelect = (accountId: string, mcId: string, feedId: string) =>
    guardedSelectionChange(() => setSelection(accountId, mcId, feedId));

  const handleClose = (options?: FeedSwitcherCloseOptions) => {
    setOpen(false);
    if (options?.restoreFocus === false) {
      // Die Pille ist auf der Zielroute /settings kein sinnvolles Fokusziel
      // mehr -- weder unser eigener Restore noch Radix' Default sollen ihn
      // dorthin zurueckreissen.
      skipCloseAutoFocusRef.current = true;
      return;
    }
    // Radix/vaul raeumen den Fokus beim Schliessen zwar selbst auf, aber
    // nicht zuverlaessig auf DIESE Pille zurueck — ohne das landet er nach
    // einem erfolgreichen Wechsel unauffindbar im Dokument.
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const handleCloseAutoFocus = (event: Event) => {
    if (skipCloseAutoFocusRef.current) {
      event.preventDefault();
      skipCloseAutoFocusRef.current = false;
    }
  };

  const mcName = merchantCenter?.name || merchantCenter?.merchant_center_id || '';
  const feedName = feed?.display_name || feed?.feed_id || '';
  const hasFeed = !!feed;
  // D10 (K09): unter lg traegt die Pille den Shop, auch ohne Feed. Fehlt der
  // Name, die GMC-ID (mcName faellt schon darauf zurueck); fehlt auch die,
  // der Feedname. Nie eine leere Pille.
  const compactName = mcName || feedName;
  const badge = hasFeed ? formatFeedBadge(feed?.target_country, feed?.content_language) : null;

  const syncTimestamp = feed?.last_synced_at || feed?.updated_at || null;
  const syncInfo = syncTimestamp
    ? (() => {
        const ageMs = Date.now() - new Date(syncTimestamp).getTime();
        const isFresh = ageMs < SYNC_FRESH_MS;
        const timeAgo = formatDistanceToNow(new Date(syncTimestamp), {
          addSuffix: false,
          locale: i18n.language === 'de' ? de : enUS,
        });
        return { isFresh, timeAgo };
      })()
    : null;

  const feedLabelForA11y = hasFeed ? feedName : t('context.switcher.noFeedYet');
  const ariaLabelBase = syncInfo
    ? t('context.switcher.labelSynced', { mc: mcName, feed: feedLabelForA11y, time: syncInfo.timeAgo })
    : t('context.switcher.label', { mc: mcName, feed: feedLabelForA11y });
  /**
   * Defekt B (Produktions-Messung 07./08.09.2026): unterhalb der `sm`-
   * Bruchstelle blendet die Pille das Laender-/Sprach-Abzeichen visuell aus
   * (siehe `pillClassName`/die Spans unten) -- die Information darf dabei
   * NICHT verloren gehen, nur nicht mehr sichtbar sein. `getFeedTooltip`
   * liefert dieselbe Info ausgeschrieben ("Deutsch (Schweiz)"), lokalisiert
   * ueber `i18n.language`; sie wird IMMER an den accessible Name angehaengt
   * -- unabhaengig von der aktuellen Fensterbreite, denn `aria-label` kennt
   * keine Media Queries und darf nicht von der sichtbaren Breite abhaengen.
   */
  const feedLanguageLabel = badge ? getFeedTooltip(feed?.target_country, feed?.content_language, i18n.language) : null;
  const ariaLabel = feedLanguageLabel ? `${ariaLabelBase}, ${feedLanguageLabel}` : ariaLabelBase;

  if (loading) {
    return <Skeleton className="h-[34px] w-[220px] rounded-xl" />;
  }

  /**
   * Durchgerechneter Platz statt geratener Klassen (Produktions-Messung
   * 07./08.09.2026, Nachmessung im Nachbau 08.09.2026, Werkzeuge unter
   * `.tmp/rahmen-mess/`: echtes Chrome ueber CDP, der echte Tailwind-Build
   * dieses Projekts, dieselben Schriften wie `index.html`).
   *
   * Die drei vorherigen Anlaeufe haben je an EINER Klasse gedreht, ohne den
   * Platzbedarf der ganzen Leiste auszurechnen. Anlauf 3 setzte
   * `min-w-[72px]`, und 72 px sind KLEINER als die Summe der nicht
   * schrumpfbaren Kinder der Pille -- fuer den Feednamen blieben gemessene
   * 2 px, und weil die Nachbarn zugleich `shrink-0` bekamen, malte die
   * Pille bei 320 px 61 px WEIT in die rechte Knopfgruppe hinein.
   *
   * BILANZ der 56-px-Leiste, in Pixeln (Rand = `px-2`/`sm:px-4`;
   * Abstaende = `gap-1.5`/`sm:gap-3` zwischen den Header-Kindern; rechte
   * Gruppe = Kapazitaetsring + Glocke + Theme + Kontomenue samt ihrer
   * eigenen Abstaende, unter `sm` 140 ohne das sichtbare Wort "Kapazität",
   * 197,1 mit ihm, 237,1 ab `sm` wegen `sm:gap-2`; Stand WP6 Task 3, K30:
   * Kapazitaetsring, Glocke und Avatar (ohne den Ausloeser links) mobil
   * je 44 px, Theme-Knopf unter `sm` im
   * Kontomenue, also 44 + 44 + 44 + 2 x 4 = 140 statt 168):
   *
   *   Breite | Rand | Ausloeser | Marke | Abstaende | rechts | Rest = Band
   *      320 |   16 |        44 |     - |        12 |    140 |         108
   *      375 |   16 |        44 |     - |        12 |    140 |         163
   *      480 |   16 |        44 |    24 |        18 |  197,1 |       180,9
   *      639 |   16 |        44 |    24 |        18 |  197,1 |       339,9
   *      640 |   32 |        44 |    24 |        36 |  237,1 |       266,9
   *      768 |   32 |         - |   210 |        24 |  237,1 |       264,9
   *     1024 |   32 |         - |   210 |        24 |  237,1 |       520,9
   *
   * Bei 320 px gewinnt die Pille 16 px (92 -> 108). Zwischen 640 und 767 px
   * ist der Ausloeser 44 statt 32 px breit, das Band verliert dort 12 px
   * (278,9 -> 266,9); der Pillenboden `xs:min-w-[128px]` reicht trotzdem.
   * (Der Ausloeser misst 44 px: `Layout.tsx` gibt ihm `h-11 w-11` mit, und
   * `cn`/twMerge laesst das gegen das `h-7 w-7` in `ui/sidebar.tsx`
   * gewinnen. Marke = kleiner Mark 24 px bzw. breites Lockup 210 px.)
   *
   * Die BOEDEN darunter, jeder als "nicht schrumpfbare Kinder + Name":
   *
   *   unter xs (<480): Polsterung 16 + Chevron 14 + 1 Abstand 6     =  36
   *   ab xs (>=480):   + Store-Icon 14 + Sync 6 + 2 Abstaende       =  68
   *   ab lg (>=1024):  + Trenner 5,4 + Abzeichen 43,3 + 3 Abstaende = 134,7
   *
   *   -> 92 (36 + 56 Name), 128 (68 + 60), 200 (134,7 + 65)
   *
   * 92 statt der angestrebten 96 (= 36 + 60 Name): dieser Wert stammt aus
   * der Zeit VOR WP6, als bei 320 px genau 92 px Band da waren. Ein
   * hoeherer Boden ueberschreitet die Bandbreite, und ein Flex-Item
   * unterschreitet seinen `min-width` NIE zugunsten des Layouts -- es
   * waechst stattdessen in den Nachbarn hinein. Nachgemessen (Stand vor
   * WP6, Band 92 px, bei 320 px): Boden 96 -> Pille ragt 4 px aus dem Band
   * (frisst den 6-px-Abstand zur rechten Gruppe auf), Boden 104 -> 2 px
   * Ueberdeckung, Boden 120 -> 18 px; damals waren 54 px Feedname das
   * Maximum. Seit WP6 Task 3 hat das Band bei 320 px 108 px (Tabelle oben):
   * der Boden 92 liegt 16 px darunter und ist bei 320 px nicht mehr die
   * bindende Grenze, die obigen Ueberdeckungswerte gelten fuer das neue
   * Band NICHT mehr und sind nicht neu gemessen. Der Boden bleibt auch
   * nach WP6 Task 5 (D10) unveraendert: unterhalb von lg steht dort der
   * MC-Name im selben Span-Budget, das vorher der Feedname hatte.
   * Ab 375 px ist der Boden ohnehin unwirksam (dort stehen 163 px zur
   * Verfuegung). Eine Breitenmessung des MC-Namens nach D10 steht aus,
   * die WP6-E2E misst bei 320, 360, 390 und 1024 px nach.
   *
   * `overflow-hidden` bleibt die Grenze, die kein Kind ueberschreiten kann
   * (Defekt B: Abzeichen und Chevron malten bis zu 109,6 px ausserhalb).
   */
  const pillClassName = [
    'inline-flex h-11 sm:h-[34px] min-w-[92px] xs:min-w-[128px] lg:min-w-[200px] items-center gap-1.5 overflow-hidden rounded-xl border px-2 text-left transition-colors',
    open ? 'border-primary bg-accent ring-[3px] ring-primary/30' : 'border-border hover:bg-accent/50',
  ].join(' ');

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      data-testid="feed-switcher-trigger"
      className={pillClassName}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ariaLabel}
    >
      {/*
        Rang 4 der Reihenfolge des Weglassens: Icon und Sync-Marker sind
        Dekoration, nicht Identitaet. Unter `xs` (480 px) kosten sie
        zusammen 32 px (14 + 6 Icon/Abstand, 6 + 6 Punkt/Abstand) -- genau
        die 32 px, die der Feedname dort braucht, um von 26 auf 58 px zu
        kommen. Ab 480 px sind sie wieder da.
      */}
      <Store className="hidden xs:block h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      {syncInfo &&
        (syncInfo.isFresh ? (
          <span
            data-testid="feed-sync-fresh"
            className="hidden xs:block h-1.5 w-1.5 shrink-0 rounded-full bg-success"
            aria-hidden="true"
          />
        ) : (
          <AlertTriangle
            data-testid="feed-sync-stale"
            className="hidden xs:block h-3 w-3 shrink-0 text-warning-ink"
            aria-hidden="true"
          />
        ))}
      {/*
        Frueher `min-w-[140px]`: ein EXPLIZITES `min-width` ist ein harter
        Boden und ueberschreibt das automatische Flex-Minimum, das `truncate`
        (overflow:hidden) sonst auf 0 senkt -- die Pille konnte deshalb nie
        unter 140px fuer diesen Span schrumpfen, egal wie schmal das Fenster
        war. `min-w-0` erlaubt das Schrumpfen bis auf die Ellipse; ohne
        eigenes `flex-*` bleibt die bevorzugte Breite weiterhin die
        Inhaltsbreite, solange Platz da ist.

        Reihenfolge des Weglassens, Bruchstelle `lg` (1024px) statt `md`
        (768px) -- durchgerechnet, nicht geraten (Nachbau-Messung
        08.09.2026, Bilanz-Tabelle bei `pillClassName` oben). Bei 768px
        stehen der Pille 264,9px zur Verfuegung; MC-Name (124,2), Trenner
        (5,4) und Abzeichen (43,3) belegen davon zusammen mit ihren
        Abstaenden 191 -- fuer den Feednamen blieben gemessene 31,3px
        (vorher-Stand: sogar nur 12,8px bei einem laengeren MC-Namen). Ein
        breiteres Fenster zeigte damit WENIGER lesbaren Text als ein
        schmaleres (bei 639px: 162,7px Feedname). Bei 1024px traegt die
        Leiste alle vier Elemente nachweislich (Pille 423,4px, MC-Name
        124,2px, Feedname 162,7px, Abzeichen 43,3px, keine Ueberdeckung).
        Pruefsatz seit D10 (UX-Audit 26.09.2026, K09): was braucht ein
        Nutzer, um zu wissen, wo er ist? Bei Agenturen den SHOP, denn Feeds
        heissen dort neunmal "Content API". Unterhalb der Bruchstelle steht
        deshalb der MC-Name im Budget, das vorher der Feedname hatte; der Feed
        steht im Popover. Ab lg sind MC, Trenner, Feed und Abzeichen sichtbar.
        Pixelrechnung Task 5: Span-Budget, Boeden (92/128/200) und
        Bruchstellen unveraendert; die Hoehe wird mobil 44 px (h-11), ab sm
        34 px, die Leiste ist 56 px hoch. Ohne Feed: Name flex-1 plus
        Warnsymbol 12 px plus das gap-1.5 der Pille (6 px; der sr-only-Text
        daneben liegt absolut und kostet keinen Platz), derselbe Boden wie
        mit Feed.
        Der aria-label bleibt unabhaengig davon vollstaendig.

        Bewusst NICHT `xl` (1280px): dort wuerde bei 1024px zwar die Journey
        wieder erscheinen (nachgemessen: Mikro-Variante passt dann), aber um
        den Preis von MC-Name und Abzeichen. Die Reihenfolge des Weglassens
        stellt die Journey VOR beide -- sie weicht zuerst, nicht zuletzt.
      */}
      {compactName && (
        <span
          data-testid="feed-switcher-mc-compact"
          className="min-w-0 flex-1 basis-10 truncate text-xs font-semibold lg:hidden"
        >
          {compactName}
        </span>
      )}
      <span data-testid="feed-switcher-mc-full" className="hidden min-w-0 shrink truncate text-[13px] font-bold lg:block">
        {mcName}
      </span>
      <span className="hidden text-muted-foreground lg:block" aria-hidden="true">
        ·
      </span>
      {hasFeed ? (
        <span
          data-testid="feed-switcher-feed-name"
          className="hidden min-w-0 flex-1 basis-10 truncate text-xs text-foreground/70 lg:block"
        >
          {feedName}
        </span>
      ) : (
        // Ohne Feed: unter lg nur das Symbol neben dem Shopnamen (Text sr-only),
        // ab lg der Text im Budget, das sonst der Feedname hat.
        <span
          data-testid="feed-switcher-no-feed"
          className="flex min-w-0 shrink-0 items-center gap-1 text-xs text-warning-ink lg:flex-1 lg:basis-10"
        >
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="sr-only lg:not-sr-only lg:truncate">{t('context.switcher.noFeedYet')}</span>
        </span>
      )}
      {badge && (
        // Dasselbe Weglassen wie beim MC-Namen, dieselbe Bruchstelle `lg`
        // (siehe Kommentar oben): das Sprach-Abzeichen ist
        // von den vier Pillen-Elementen das entbehrlichste (die Sprache
        // steht ohnehin im `aria-label`), deshalb faellt es zuerst -- aber
        // zusammen mit MC-Name/Trenner, nicht an einer eigenen Bruchstelle,
        // sonst tauchen sie nacheinander an verschiedenen Breiten auf und
        // druecken den Feednamen jedes Mal erneut zusammen.
        <span className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide lg:inline-flex">
          {badge}
        </span>
      )}
      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );

  const list = (
    <FeedSwitcherList
      accounts={accounts}
      isLoading={treeLoading}
      isError={treeError}
      detailsFailed={detailsFailed}
      selectedMerchantCenterId={selectedMerchantCenterId}
      selectedFeedId={selectedFeedId}
      onSelect={handleSelect}
      onClose={handleClose}
      autoFocusInput={!isDrawer}
    />
  );

  if (isDrawer) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent className="max-h-[85dvh]" onCloseAutoFocus={handleCloseAutoFocus}>
          {/* Radix/vaul verlangt einen Titel fuer den Accessible Name des Dialogs;
              sichtbar ist er nicht, den traegt die Pille bereits. */}
          <DrawerTitle className="sr-only">{t('context.switcher.popoverLabel')}</DrawerTitle>
          {list}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      {/* Radix rendert PopoverContent mit role="dialog" (I4, Fix-Runde 2):
          ohne eigenes aria-label traegt der Dialog keinen accessible Name --
          `Command`s aria-label sitzt zwar innen, ist aber der Name des
          `[role=listbox]`-Nachfahren, nicht des Dialog-Containers selbst. */}
      <PopoverContent
        align="start"
        className="w-[460px] p-0"
        aria-label={t('context.switcher.popoverLabel')}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        {list}
      </PopoverContent>
    </Popover>
  );
};
